import api from "@/api";

import FileSaver from "file-saver";
import deepClone from "@/utils/deep-clone";
import Vue from "vue";

const state = {
    topics: [],
    list: [],
    hasMore: true,
    minObsAnonymity: 3,
    detail: {},
};

const getters = {
    list(state) {
        return state.list;
    },
    topics(state) {
        return state.topics;
    },
    hasMore(state) {
        return state.hasMore;
    },
    detail(state) {
        return id => state.detail[id] || {};
    },
    minObsAnonymity(state) {
        return state.minObsAnonymity;
    },
};

const actions = {
    get({ commit }, { payload }) {
        return api.post(`/api/surveys/get/details/signals`, payload).then(res => {
            const data = res?.data ?? {};

            commit("updateComments", { data, page: payload.page }); // update data
        });
    },
    getDetail({ commit }, { payload: { surveyId, commentId } }) {
        return api.get(`/api/surveys/${surveyId}/comments/${commentId}`).then(res => {
            const data = res?.data ?? {};

            commit("updateDetail", { data }); // update data
        });
    },
    export(_, { payload }) {
        return api
            .post(`/api/surveys/get/details/signals`, payload, { responseType: "arraybuffer" })
            .then(res => {
                const date = new Date();
                const filename =
                    "Survey-Export-Comments-" + date.toLocaleDateString("de") + ".xlsx";

                FileSaver.saveAs(
                    new Blob([res.data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    }),
                    filename,
                );
            });
    },
    create({ dispatch }, { payload }) {
        return api.post(`/api/surveys/comments`, payload).then(() => {
            dispatch({
                type: "getDetail",
                payload: { surveyId: payload.survey, commentId: payload.surveySignal },
            });
        });
    },
    markAsSeen({ commit }, { payload: { surveyId, signalId, isMarkedAsSeen } } = { payload: {} }) {
        return api
            .post(`/api/surveys/${surveyId}/signals/${signalId}/seen`, {
                isMarkedAsSeen,
            })
            .then(({ data }) => {
                commit("updateSingleListEntry", data);
            });
    },
    reaction(
        { commit },
        { payload: { surveyId, signalId, type, isCreate, currentUser } } = { payload: {} },
    ) {
        const oldDetail = getters.detail(state)(signalId);
        const oldData = state.list.find(comment => comment._id === signalId);

        const newData = deepClone(oldData);
        if (isCreate) {
            newData.myReactions.push(type);
        } else {
            newData.myReactions = newData.myReactions.filter(reaction => reaction != type);
        }
        commit("updateSingleListEntry", newData);

        const newDetail = deepClone(oldDetail);
        if (oldDetail.reaction) {
            if (isCreate) {
                newDetail.reaction[type].push(currentUser);
                newDetail.myReactions.push(type);
            } else {
                newDetail.reaction[type] = newDetail.reaction[type].filter(
                    user => user._id != currentUser._id,
                );
                newDetail.myReactions = newData.myReactions.filter(reaction => reaction != type);
            }
            commit("updateDetail", { data: newDetail });
        }

        return api
            .post(`/api/surveys/${surveyId}/signals/${signalId}/reaction`, {
                type,
                isCreate,
            })

            .catch(() => {
                // recover
                commit("updateSingleListEntry", oldData);
                commit("updateDetail", {
                    data: oldData,
                });
            });
    },
};

const mutations = {
    updateComments(state, { data, page }) {
        if (data.hasMore != undefined) {
            state.hasMore = data.hasMore;
        }
        if (data.topics && data.topics.length) {
            state.topics = data.topics;
        }
        if (data.minObsAnonymity) {
            state.minObsAnonymity = data.minObsAnonymity;
        }
        if (data.data) {
            if (page === 0) {
                state.list = data.data;
            } else {
                state.list.push(...data.data); // append
            }
        }
    },
    updateSingleListEntry(state, data) {
        const index = state.list.findIndex(comment => comment._id === data._id);

        if (index === -1) return;

        Vue.set(state.list, index, { ...state.list[index], ...data });
    },
    updateDetail(state, { data }) {
        state.detail = {
            ...state.detail,
            [data._id]: { ...state.detail[data._id], ...data },
        };
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
